import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { GlobeAltIcon, AcademicCapIcon, ComputerDesktopIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';

const DistanceEducationIntro: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 pt-20 bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
          <div className="max-w-7xl mx-auto px-6">
            <h1 className="text-5xl font-bold mb-6">교육원 소개</h1>
            <p className="text-xl text-blue-100">글로벌 감성, 미래인재의 도시 동두천</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-16">
          {/* Introduction */}
          <div className="bg-white rounded-xl shadow-sm p-10 mb-12">
            <div className="prose max-w-none">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                세계와 소통하는 글로벌 교육도시를 지향하는 동두천시는 ㈜한원글로벌이 운영하는
                <strong className="text-blue-600"> '동두천글로벌인재교육센터 원격평생교육원'</strong>을 통해
                시간과 장소의 제약 없이 누구나 참여할 수 있는 온라인 글로벌 교육 기회를 제공합니다.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                ICT 기반의 첨단 원격학습 시스템을 활용하여 영어, 글로벌 문화, AI 융합교육 등
                다양한 분야의 학습과정을 운영하며 지역민의 국제 역량 강화와 평생학습 사회 실현에 앞장섭니다.
              </p>
            </div>
          </div>

          {/* Main Education Areas */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">주요 교육 분야</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {/* 영어회화 과정 */}
              <div className="bg-white rounded-xl shadow-sm p-8 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <ChatBubbleLeftRightIcon className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">영어회화 과정</h3>
                    <p className="text-gray-600 leading-relaxed">
                      실생활 중심의 주제별 회화 및 문화 교류를 통해 자연스러운 표현력 향상
                    </p>
                  </div>
                </div>
              </div>

              {/* AI 영어 융합 과정 */}
              <div className="bg-white rounded-xl shadow-sm p-8 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <ComputerDesktopIcon className="h-6 w-6 text-purple-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">AI 영어 융합 과정</h3>
                    <p className="text-gray-600 leading-relaxed">
                      AI·STEAM 융합 프로젝트를 영어로 배우며 창의적 문제 해결력 강화
                    </p>
                  </div>
                </div>
              </div>

              {/* 리딩랩 */}
              <div className="bg-white rounded-xl shadow-sm p-8 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <AcademicCapIcon className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">리딩랩(Reading Horizons)</h3>
                    <p className="text-gray-600 leading-relaxed">
                      온라인 영어 독서 및 어휘력 향상 프로그램을 통한 자기주도적 학습
                    </p>
                  </div>
                </div>
              </div>

              {/* 글로벌 리더십 세미나 */}
              <div className="bg-white rounded-xl shadow-sm p-8 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                      <GlobeAltIcon className="h-6 w-6 text-orange-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">글로벌 리더십 세미나</h3>
                    <p className="text-gray-600 leading-relaxed">
                      외국인 강사와 함께하는 온라인 토론 및 발표 중심의 리더십 강화 프로그램
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-10 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              시간과 장소의 제약 없이 글로벌 역량을 키워보세요
            </h3>
            <p className="text-gray-600 mb-6">
              동두천글로벌인재교육센터 원격평생교육원과 함께 미래를 준비하세요
            </p>
            <div className="flex gap-4 justify-center">
              <a
                href="/distance-education/rules"
                className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                운영 규칙 보기
              </a>
              <a
                href="/application/register"
                className="inline-block bg-white text-blue-600 px-8 py-3 rounded-lg font-medium border-2 border-blue-600 hover:bg-blue-50 transition-colors"
              >
                수강 신청하기
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DistanceEducationIntro;
